import Breadcrumb from '@components/Shared/Breadcrumb';
import { Card, CardBody } from '@components/card/card';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Loading from '@components/Shared/Loader/Loading';
import useGetReceiptsList from '@hooks/AR/ArReceipts/useGetReceiptsList';
import NtxTable from '@components/DataTable/NtxTable';
import { receiptTypeEnum } from './receiptTypeEnum';
import useGetReceiptsTypeList from '@hooks/Finance/ArReceipts/useGetReceiptsTypeList';

const Index = () => {
  const { t, i18n } = useTranslation();

  const { ArReceiptsList: data, loading, error } = useGetReceiptsList();

  const { ReceiptsTypeList } = useGetReceiptsTypeList();

  // دالة لتنسيق المبلغ
  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat(i18n.language, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getReceiptTypeDisplay = (type: string) => {
    const name =
      i18n.language === 'en'
        ? ReceiptsTypeList.find(f => f.id === type)?.name
        : ReceiptsTypeList.find(f => f.id === type)?.name2;
    switch (type) {
      case receiptTypeEnum.Standard: // ✅ قياسي
        return (
          <span className='badge bg-primary'>
            <i className='fas fa-file-invoice-dollar me-1'></i>
            {name}
          </span>
        );

      case receiptTypeEnum.PreReceipt: // ✅ دفعة مقدمة
        return (
          <span className='badge bg-success'>
            <i className='fas fa-hand-holding-usd me-1'></i>
            {name}
          </span>
        );

      case receiptTypeEnum.Refund: // ✅ استرداد
        return (
          <span className='badge bg-danger'>
            <i className='fas fa-rotate-left me-1'></i>
            {name}
          </span>
        );

      case receiptTypeEnum.ApplyPreReceipt: // ✅ تطبيق دفعة مقدمة
        return (
          <span className='badge bg-info text-dark'>
            <i className='fas fa-link me-1'></i>
            {name}
          </span>
        );

      case receiptTypeEnum.ApplyCreditMemo: // ✅ تطبيق إشعار دائن
        return (
          <span className='badge bg-warning text-dark'>
            <i className='fas fa-file-invoice me-1'></i>
            {name}
          </span>
        );

      default:
        return (
          <span className='badge bg-secondary'>
            <i className='fas fa-question-circle me-1'></i>
            {name}
          </span>
        );
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return (
          <span className='badge bg-success'>
            <i className='fas fa-check-circle me-1'></i>
            {t('مكتمل')}
          </span>
        );
      case 'cancelled':
        return (
          <span className='badge bg-danger'>
            <i className='fas fa-times-circle me-1'></i>
            {t('ملغي')}
          </span>
        );
      case 'posted':
        return (
          <span className='badge bg-primary'>
            <i className='fas fa-paper-plane me-1'></i>
            {t('Posted')}
          </span>
        );
      case 'draft':
        return (
          <span className='badge bg-secondary'>
            <i className='fas fa-edit me-1'></i>
            {t('Draft')}
          </span>
        );
      case 'pending':
        return (
          <span className='badge bg-warning text-dark'>
            <i className='fas fa-clock me-1'></i>
            {t('Pending')}
          </span>
        );
      default:
        return (
          <span className='badge bg-light text-dark'>
            <i className='fas fa-circle me-1'></i>
            {status || t('Unknown')}
          </span>
        );
    }
  };

  // دالة لتنسيق رقم السند بناءً على الحالة
  const formatVoucherNumber = (code: string, status?: string) => {
    const isCancelled = status?.toLowerCase() === 'cancelled';

    if (isCancelled) {
      return (
        <span className='text-danger text-decoration-line-through fw-bold' style={{ letterSpacing: '0.09em' }}>
          {code}
        </span>
      );
    }

    return (
      <span className='fw-bold' style={{ letterSpacing: '0.09em' }}>
        {code}
      </span>
    );
  };

  const renderDescription = (text?: string | null) => {
    const v = (text ?? '').trim();
    if (!v) return <span className='text-muted'>-</span>;

    const max = 60;
    if (v.length <= max) return <span>{v}</span>;

    return <span title={v}>{v.slice(0, max)}…</span>;
  };

  const columns = [
    { title: t('ReceiptNumber'), key: 'code' },
    { title: t('Date'), key: 'receiptDate' },
    { title: t('ReceiptType'), key: 'Type' },
    { title: t('description'), key: 'description' },
    { title: t('Amount'), key: 'amount', className: 'text-theme' },
    { title: t('Customer'), key: 'peopleName' },
    { title: t('وحدة التشغيـل'), key: 'organizationName' },
    { title: t('Status'), key: 'status' },
    {
      title: t('PostingStatus'),
      key: 'postedFlagDisplay',
      className: 'text-center',
      sortValue: (row: any) => (row.postedFlag ? 1 : 0)
    },
    { title: '', key: 'action', className: 'text-end' }
  ];

  const makeReceiptCodeSortKey = (code?: string | null) => {
    if (!code) return 0;

    const v = String(code).trim();

    // AR202628 => Prefix + YYYY + Seq
    const m = v.match(/^([A-Za-z]+)(\d{4})(\d+)$/);
    if (m) {
      const year = Number(m[2]); // 2026
      const seq = Number(m[3]); // 28
      return year * 1_000_000_000 + seq;
    }

    // fallback
    const digits = (v.match(/\d+/g) || []).join('');
    return digits ? Number(digits) : 0;
  };

  

  const tableData = (data ?? [])
    .slice()
    .sort((a, b) =>makeReceiptCodeSortKey(b.code) - makeReceiptCodeSortKey(a.code)  )
    .map(item => {
      const postedFlagBadge = item.postedFlag ? (
        <span className='badge bg-success'>
          <i className='fas fa-check me-1'></i>
          {t('Posted')}
        </span>
      ) : (
        <span className='badge bg-danger'>
          <i className='fas fa-times me-1'></i>
          {t('NotPosted')}
        </span>
      );

      return {
        code: formatVoucherNumber(item.code, item?.status),
        receiptDate: item.depositDate?.split('T')[0] || item.depositDate?.split('T')[0],
        Type: getReceiptTypeDisplay(item.paymentType),
        // ✅ جديد
        description: renderDescription(item.description),
        amount: <span className='fw-bold text-theme'>{formatAmount(item.amount)}</span>,
        peopleName: i18n.language === 'en' ? item.peopleName : item.peopleName2,
        organizationName: i18n.language === 'en' ? item.organizationName : item.organizationName2,
        status: getStatusBadge(item.status),
        postedFlag: item.postedFlag,
        postedFlagDisplay: postedFlagBadge,
        action: (
          <Link to={`${item.id}/UpdateArReceipts`} className='btn btn-sm btn-outline-theme mx-1'>
            <i className='fas fa-info-circle fa-lg'></i>
          </Link>
        )
      };
    });

  return (
    <div>
      <div className='d-flex align-items-center'>
        <Breadcrumb
          BreadcrumbList={[
            {
              name: 'Home',
              link: ':moduleHome'
            },
            {
              name: 'Receipts',
              link: null
            }
          ]}
          PageName='Receipts'
        />
        <div className='ms-auto'>
          <Link to='AddArReceipts' className='btn me-1 btn-theme'>
            <i className='fa fa-plus-circle fa-fw me-1'></i>
            {t('NewReceipt')}
          </Link>
        </div>
      </div>
      <Card>
        <Loading loading={loading} error={error}>
          <CardBody>
            <NtxTable
              data={tableData || []}
              columns={columns}
              dataPerPage={10}
              theadClass=''
              tableClass='table text-nowrap table-hover'
              enableSearch={true}
              showRecordsSelector={true}
            />
          </CardBody>
        </Loading>
      </Card>
    </div>
  );
};

export default Index;
